// components/AppUiLayer.tsx
"use client";

import React, { useCallback, useEffect } from 'react';
import type { SavedPromptEntry } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import UserSettingsModal from '@/components/UserSettingsModal';
import ActionHistoryModal from '@/components/ActionHistoryModal';
import AssistantDebugModal from '@/components/AssistantDebugModal';
import ImageCropModal from '@/components/ImageCropModal';
import ProjectTypeSelectionModal from '@/components/ProjectTypeSelectionModal';
import SaveStatusModal from '@/components/SaveStatusModal';
import DetachedStateClarificationModal from '@/components/DetachedStateClarificationModal';
import LayerDeletionConfirmationModal from '@/components/LayerDeletionConfirmationModal';
import PromptLibraryModal from '@/components/PromptLibraryModal';
import ImageExportModal from '@/components/ImageExportModal';
import ModelGalleryModal from '@/components/ModelGalleryModal';
import SupportChatbot from '@/components/SupportChatbot';
import NotificationCenterPopover from '@/components/notifications/NotificationCenterPopover';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';
import ConfirmationModal from '@/components/ConfirmationModal';
import { useKernelStore, useKernelStoreApi } from '@/kernel/kernelStores';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import { useActionHistorySync } from '@/kernel/useActionHistorySync';
import {
  findStudioToolShortcutByCommand,
  isInertSurface,
  keyboardEventToShortcutChord,
  shouldIgnoreKeyboardShortcutEvent,
} from '#lib/keyboard-shortcuts';

export default function AppUiLayer() {
  const { kernel } = useKernel();
  const activeModal = useKernelStore((state) => state.activeModal);
  const modalPayload = useKernelStore((state) => state.modalPayload);
  const projectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const assistantTurnDebugLog = useKernelStore((state) => state.assistantTurnDebugLog);
  const kernelStore = useKernelStoreApi();

  useActionHistorySync({
    projectId: projectId ?? null,
    chatSessionId: activeChatSessionId ?? null,
  });

  const handleCloseModal = useCallback(() => {
    if (kernel.current) {
      kernel.current.closeModal();
      return;
    }
    const state = kernelStore.getState();
    state.setActiveModal(null);
    state.setModalPayload(null);
  }, [kernel, kernelStore]);

  const handleSelectPromptFromLibrary = useCallback((entry: SavedPromptEntry) => {
    if (entry.prompt) {
      kernel.current?.setChatPrompt(entry.prompt);
    }
    handleCloseModal();
  }, [kernel, handleCloseModal]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const state = kernelStore.getState();
      if (shouldIgnoreKeyboardShortcutEvent(event, state.activeModal)) return;

      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const keyIdentifier = keyboardEventToShortcutChord(event, isMac);

      const commandToRun = state.userPreferences.shortcuts?.[keyIdentifier];
      if (!commandToRun) {
        return;
      }

      const canvasElement = document.getElementById('editor-canvas');
      // Kept mounted under its project's 3D scene, the canvas is not a Studio canvas context: the keys are the scene's.
      const canvasProjectId = isInertSurface(canvasElement) ? '' : canvasElement?.dataset.projectId?.trim() ?? '';
      const isStudioToolCommand = Boolean(findStudioToolShortcutByCommand(commandToRun));
      const hasCommandModifier = event.ctrlKey || event.metaKey || event.altKey;
      const requiresStudioCanvas = isStudioToolCommand || !hasCommandModifier;
      if (requiresStudioCanvas && !canvasProjectId) return;

      const currentProjectId = selectActiveSessionProjectIdFromInventoryState(state);
      const normalizedProjectId = canvasProjectId
        || (typeof currentProjectId === 'string' ? currentProjectId.trim() : '');
      if (!normalizedProjectId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      kernel.current?.executeSlashCommandDirectly(commandToRun, normalizedProjectId);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [kernel, kernelStore]);

  return (
    <>
      {activeModal === 'UserSettings' && (
        <UserSettingsModal />
      )}
      {activeModal === 'ActionHistory' && (
        <ActionHistoryModal />
      )}
      {activeModal === 'AssistantDebug' && (
        <AssistantDebugModal
          isOpen={true}
          onClose={handleCloseModal}
          debugInfo={assistantTurnDebugLog.find(log => log.turnId === modalPayload?.turnId) || null}
        />
      )}
      {activeModal === 'ImageCrop' && (
        <ImageCropModal
          isOpen={activeModal === 'ImageCrop'}
          imageFile={modalPayload?.imageFile}
          alternateImageFile={modalPayload?.alternateImageFile}
          initialPolygon={modalPayload?.initialPolygon}
          imageIndex={modalPayload?.imageIndex}
          onClose={handleCloseModal}
          onConfirm={modalPayload?.onConfirm}
          sourceRect={modalPayload?.sourceRect}
          instruction={modalPayload?.instruction}
          outputMode={modalPayload?.outputMode}
          autoConfirmOnPreview={modalPayload?.autoConfirmOnPreview}
        />
      )}
      {activeModal === 'ProjectTypeSelection' && (
        <ProjectTypeSelectionModal />
      )}
      {activeModal === 'SaveStatus' && (
        <SaveStatusModal />
      )}
      {activeModal === 'DetachedStateClarification' && modalPayload && (
        <DetachedStateClarificationModal />
      )}
      {activeModal === 'LayerDeletionConfirmation' && (
        <LayerDeletionConfirmationModal
          isOpen={true}
          layerName={modalPayload?.layerName ?? ''}
          onConfirm={modalPayload?.onConfirm ?? handleCloseModal}
          onCancel={modalPayload?.onCancel ?? handleCloseModal}
        />
      )}
      {activeModal === 'PromptLibrary' && (
        <PromptLibraryModal
          isOpen={true}
          onClose={handleCloseModal}
          onSelectPrompt={handleSelectPromptFromLibrary}
        />
      )}
      {activeModal === 'ImageExport' && (
        <ImageExportModal
          isOpen={true}
          projectId={modalPayload?.projectId ?? projectId ?? ''}
          payload={modalPayload}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'ModelGallery' && (
        <ModelGalleryModal
          isOpen={true}
          payload={modalPayload}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'SupportChat' && (
        <SupportChatbot onClose={handleCloseModal} />
      )}
      {activeModal === 'NotificationCenter' && (
        <NotificationCenterPopover onClose={handleCloseModal} />
      )}
      {activeModal === 'DocumentPreview' && modalPayload?.url && (
        <DocumentPreviewModal url={modalPayload.url} title={modalPayload.title ?? 'Document'} onClose={handleCloseModal} />
      )}
      {activeModal === 'Confirmation' && modalPayload && (
        <ConfirmationModal payload={modalPayload} onClose={handleCloseModal} />
      )}
    </>
  );
}
